/**
 * @fileoverview Hook for detecting potential companion cases during ingestion.
 *
 * Provides suggestions based on:
 * 1. Minor detection: Client is under 18 years old (may have parent/guardian case)
 * 2. Similar names: Other matters from same firm with similar last name, created in past 48 hours
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useGetMattersQuery } from '@/services/api/mattersService';
import type { BaseMatter } from '@/services/types/client-intake-types';

/**
 * Calculate age from date of birth string.
 */
function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

export interface PotentialCompanionsResult {
  /** True if client is under 18 years old */
  isMinor: boolean;
  /** Matters from same firm with similar last name created in past 48 hours */
  similarMatters: BaseMatter[];
  /** True while loading similar matters query */
  isLoading: boolean;
  /** True if there are any suggestions to show */
  hasSuggestions: boolean;
}

/**
 * Hook that detects potential companion cases based on client data.
 *
 * @param matterId - Current matter ID (to exclude from results)
 * @param clientDob - Client's date of birth (for minor detection)
 * @param clientLastName - Client's last name (for similar name matching)
 * @param firmId - Firm ID (to filter similar matters to same firm)
 */
export function usePotentialCompanions(
  matterId: string | null,
  clientDob: string | null,
  clientLastName: string | null,
  firmId: string | null,
): PotentialCompanionsResult {
  // 1. Minor detection (purely client-side calculation)
  const isMinor = useMemo(() => {
    if (!clientDob) return false;
    return calculateAge(clientDob) < 18;
  }, [clientDob]);

  // 2. Similar names query - search matters by last name within the same firm
  const { data: mattersData, isLoading } = useGetMattersQuery(
    firmId && clientLastName
      ? { firm: [firmId], search: clientLastName, limit: 20 }
      : skipToken,
  );

  // Filter to recent matters (created in past 48 hours)
  const similarMatters = useMemo(() => {
    if (!mattersData?.results) return [];

    const fortyEightHoursAgo = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();

    return mattersData.results.filter((m) => {
      // Exclude current matter
      if (m.id === matterId) return false;
      // Exclude non-primary members of a companion group
      // Primary matters should still be suggested - user can link to their group
      if (m.companion_root_id && !m.is_companion_primary) return false;
      // Filter to recent (created in past 48 hours)
      if (m.created_at && m.created_at < fortyEightHoursAgo) return false;
      return true;
    });
  }, [mattersData, matterId]);

  const hasSuggestions = isMinor || similarMatters.length > 0;

  return {
    isMinor,
    similarMatters,
    isLoading,
    hasSuggestions,
  };
}
